import type { Vec2, Vec3, Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { planPoint, prismSolid, pyramidOverRing, type UvOf } from '../solids';
import { pointedArchRise, pointedWindow, verticalPanel, type Panel } from '../panels';
import type { WallFace } from '../houseFacades';
import { glazedWall, type Glazing } from './tracery/glazing';
import { plainWall } from './wallCoords';

/** Masonry wall, slate roof and dark window openings of one landmark. */
export interface MasonryStyles {
  readonly wall: VertexStyle;
  readonly roof: VertexStyle;
  readonly opening: VertexStyle;
  /** Glass set back behind stone reveals, and the tracery's sink; without it openings are drawn flush. */
  readonly glazing?: Glazing;
}

export const WINDOW = {
  archSegments: 3,
  /** Window width as a share of its bay. */
  share: 0.42,
  maxWidth: 3.2,
  minJamb: 1.2,
} as const;

function length2(a: Vec2, b: Vec2): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/** Vertical panel on the outside of a counter-clockwise plan edge: a runs from→to, b up from y0. */
export function edgePanel(from: Vec2, to: Vec2, y0 = 0): Panel {
  const len = length2(from, to);
  const du = (to[0] - from[0]) / len;
  const dv = (to[1] - from[1]) / len;
  return verticalPanel(planPoint(from[0], y0, from[1]), [dv, 0, du]);
}

/** The wall face on the outside of a plan edge, its panel standing on y0. */
export function edgeFace(from: Vec2, to: Vec2, y0 = 0): WallFace {
  const len = length2(from, to);
  return { panel: edgePanel(from, to, y0), outward: [(to[1] - from[1]) / len, 0, (to[0] - from[0]) / len] };
}

/** A wall face with its openings: glazed behind reveals where the styles carry glazing, else flush; patterned faces carry their own coordinates. */
export function openedWall(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  panel: Panel,
  outline: Ring,
  openings: readonly Ring[]
): void {
  if (styles.glazing && openings.length > 0) {
    const glazed = { wall: styles.wall, opening: styles.opening, glazing: styles.glazing };
    glazedWall(builder, glazed, frame, panel, outline, openings, WINDOW.archSegments);
    return;
  }
  plainWall(builder, styles, frame, panel, outline, openings);
}

/**
 * Rectangular wall between two heights. Panel coordinates (and the facade UV the shader's
 * window rows read) count up from `base`, so an upper storey can keep ground-based storeys.
 */
export function rectWall(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  edge: readonly [Vec2, Vec2],
  heights: readonly [number, number],
  openings: readonly Ring[] = [],
  base: number = heights[0]
): void {
  const len = length2(edge[0], edge[1]);
  const b0 = heights[0] - base;
  const b1 = heights[1] - base;
  const outline: Ring = [
    [0, b0],
    [len, b0],
    [len, b1],
    [0, b1],
  ];
  openedWall(builder, styles, frame, edgePanel(edge[0], edge[1], base), outline, openings);
}

/** Wall to eaveY with a gable triangle rising to apexY over the middle of the edge. */
export function gableWall(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  edge: readonly [Vec2, Vec2],
  heights: { readonly eaveY: number; readonly apexY: number },
  openings: readonly Ring[] = []
): void {
  const len = length2(edge[0], edge[1]);
  const outline: Ring = [
    [0, 0],
    [len, 0],
    [len, heights.eaveY],
    [len / 2, heights.apexY],
    [0, heights.eaveY],
  ];
  openedWall(builder, styles, frame, edgePanel(edge[0], edge[1]), outline, openings);
}

/** End wall under a single slope: height hFrom at the edge start, hTo at its end. */
export function slopedWall(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  edge: readonly [Vec2, Vec2],
  hFrom: number,
  hTo: number
): void {
  const len = length2(edge[0], edge[1]);
  const outline: Ring = [
    [0, 0],
    [len, 0],
    [len, hTo],
    [0, hFrom],
  ];
  plainWall(builder, styles, frame, edgePanel(edge[0], edge[1]), outline, []);
}

/**
 * One pointed window per bay along a wall of the given length, sill to arch top within
 * [sill, top]. Walls too low for a window with proper jambs get none.
 */
export function pointedWindows(length: number, bay: number, sill: number, top: number): Ring[] {
  const count = Math.max(1, Math.round(length / bay));
  const width = Math.min(WINDOW.maxWidth, (WINDOW.share * length) / count);
  const spring = top - pointedArchRise(width);
  if (spring - sill < WINDOW.minJamb) return [];
  return Array.from({ length: count }, (_, k) =>
    pointedWindow(((k + 0.5) * length) / count, sill, width, spring, WINDOW.archSegments)
  );
}

/** One tall pointed window centred on a wall face of the given width. */
export function centredWindow(width: number, sill: number, top: number, share: number): Ring[] {
  const w = Math.min(WINDOW.maxWidth * 1.6, share * width);
  const spring = top - pointedArchRise(w);
  return spring - sill < WINDOW.minJamb ? [] : [pointedWindow(width / 2, sill, w, spring, WINDOW.archSegments)];
}

export interface ButtressSpec {
  readonly width: number;
  readonly depth: number;
  readonly height: number;
  readonly pinnacle: number;
}

/** Masonry pier standing against a wall at plan point `at`, projecting along `outward`. */
export function buttress(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  at: Vec2,
  outward: Vec2,
  spec: ButtressSpec
): void {
  const along: Vec2 = [-outward[1], outward[0]];
  const w = spec.width / 2;
  const corner = (s: number, d: number): Vec2 => [
    at[0] + along[0] * s * w + outward[0] * d,
    at[1] + along[1] * s * w + outward[1] * d,
  ];
  const ring: Ring = [corner(-1, -0.1), corner(1, -0.1), corner(1, spec.depth), corner(-1, spec.depth)];
  prismSolid(builder, styles.wall, frame, ring, 0, spec.height, { top: spec.pinnacle <= 0, bottom: false });
  if (spec.pinnacle > 0) {
    const tip = { plan: corner(0, spec.depth / 2), y: spec.height + spec.pinnacle };
    pyramidOverRing(builder, { roof: styles.roof, wall: styles.wall }, frame, ring, spec.height, tip);
  }
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function unit(a: Vec3): Vec3 {
  const l = Math.hypot(a[0], a[1], a[2]);
  return [a[0] / l, a[1] / l, a[2] / l];
}

/** Roof UV in metres: along the eave line and up the slope, so slate courses run level. */
export function roofUv(eaveA: Vec3, eaveB: Vec3, upper: Vec3): UvOf {
  const e = unit(sub(eaveB, eaveA));
  const w = sub(upper, eaveA);
  const along = dot(w, e);
  const up = unit([w[0] - e[0] * along, w[1] - e[1] * along, w[2] - e[2] * along]);
  return (p) => {
    const d = sub(p, eaveA);
    return [dot(d, e), dot(d, up)];
  };
}
